import { RippCore } from "../components/v2/RippCore";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutGrid, Waves, TrendingUp, CalendarDays, Gauge, Activity, Settings2,
  ArrowLeft, ChevronDown, Radio,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";
import { useAgentSocket } from "../hooks/useAgentSocket";
import { fmt } from "../lib/format";

/* ───────────────────────── palette ───────────────────────── */
const C = {
  void: "#030406",
  cyan: "#00F0FF",
  teal: "#00E5B5",
  magenta: "#FF00FF",
  red: "#FF2A2A",
  neutral: "#8A94A6",
  acid: "#38BDF8",
};
const TFS = ["1S", "15S", "1m", "5m", "15m", "1H", "4H", "D", "W", "M"];
const TF_MAP = { "5m": "5m", "15m": "15m", "1H": "1h" };
const dec = (p) => (p != null && p > 1000 ? 1 : p != null && p < 2 ? 4 : 2);

/* ───────────────────────── left icon rail ───────────────────────── */
const RAIL = [
  { k: "overview", label: "Overview", Icon: LayoutGrid },
  { k: "liquid", label: "Liquid Map", Icon: Waves },
  { k: "futures", label: "Futures", Icon: TrendingUp },
  { k: "events", label: "Events", Icon: CalendarDays },
  { k: "sentiment", label: "Sentiment", Icon: Gauge },
  { k: "flow", label: "Flow", Icon: Activity },
];
const LeftRail = ({ active, onNav, coreMode = "idle" }) => (
  <div className="w-16 flex-shrink-0 flex flex-col items-center py-5 gap-2 border-r border-white/10 bg-[#050709] z-40" data-testid="engine-left-rail">
    <a href="/v2" title="RIPP 2.0 command center" data-testid="engine-core" className="grid h-10 w-10 place-items-center mb-3"><RippCore mode={coreMode} agents={[]} size={44} compact /></a>
    {RAIL.map(({ k, label, Icon }) => (
      <button key={k} title={label} data-testid={`rail-${k}`} onClick={() => onNav(k)}
        className={`group relative grid h-10 w-10 place-items-center rounded-lg transition-colors duration-200 ${active === k ? "text-[#38BDF8]" : "text-zinc-600 hover:text-zinc-300"}`}>
        {active === k && <span className="absolute left-0 h-6 w-0.5 rounded-r bg-[#38BDF8] shadow-[0_0_8px_#38BDF8]" />}
        <Icon size={18} strokeWidth={1.6} />
      </button>
    ))}
    <div className="mt-auto">
      <button title="Settings" className="grid h-10 w-10 place-items-center rounded-lg text-zinc-600 hover:text-zinc-300 transition-colors">
        <Settings2 size={18} strokeWidth={1.6} />
      </button>
    </div>
  </div>
);

/* ───────────────────────── forecast cards ───────────────────────── */
const CONF = (v) => (v == null ? "—" : v >= 0.66 ? "HIGH" : v >= 0.4 ? "MEDIUM" : "LOW");
const cardMeta = {
  BULL: { name: "BULLISH PATH", col: C.teal, border: "border-l-[#00E5B5]" },
  BASE: { name: "NEUTRAL PATH", col: C.neutral, border: "border-l-[#8A94A6]" },
  BEAR: { name: "BEARISH PATH", col: C.red, border: "border-l-[#FF2A2A]" },
};
const ForecastCard = ({ sc, confidence, primary, i }) => {
  const meta = cardMeta[sc.type];
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.08, duration: 0.5 }}
      data-testid={`forecast-card-${sc.type.toLowerCase()}`}
      className={`pointer-events-auto rounded-xl border border-white/10 ${meta.border} border-l-[3px] bg-black/50 backdrop-blur-2xl p-3.5 shadow-[0_8px_32px_rgba(0,0,0,0.6)] transition-transform duration-200 hover:-translate-y-1 ${sc.type === primary ? "ring-1 ring-white/10" : ""}`}
      style={{ boxShadow: sc.type === primary ? `0 0 24px ${meta.col}22` : undefined }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: meta.col }}>{meta.name}</span>
        <span className="text-xl font-black font-mono tracking-tight" style={{ color: meta.col }}>{sc.prob}%</span>
      </div>
      <div className="mt-2 h-1 w-full rounded-full bg-white/5 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: meta.col }}
          initial={{ width: 0 }} animate={{ width: `${sc.prob}%` }} transition={{ delay: 0.4 + i * 0.08, duration: 0.7 }} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <div className="text-[8.5px] font-mono uppercase tracking-[0.16em] text-zinc-500">Target</div>
          <div className="text-sm font-bold font-mono text-white">{fmt(sc.target, dec(sc.target))}</div>
        </div>
        <div>
          <div className="text-[8.5px] font-mono uppercase tracking-[0.16em] text-zinc-500">Confidence</div>
          <div className="text-sm font-bold font-mono" style={{ color: meta.col }}>{CONF(confidence)}</div>
        </div>
      </div>
    </motion.div>
  );
};

/* ───────────────────────── bottom panel shell ───────────────────────── */
const PanelShell = ({ title, right, children, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-white/10 bg-black/40 backdrop-blur-xl p-3 flex flex-col overflow-hidden">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[9.5px] font-mono uppercase tracking-[0.2em] text-zinc-400">{title}</span>
      {right}
    </div>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

/* ───────────────────────── sentiment radial gauge ───────────────────────── */
const SentimentGauge = ({ score, subs }) => {
  const r = 34, circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const col = pct >= 60 ? C.teal : pct >= 40 ? C.acid : C.red;
  const label = pct >= 60 ? "BULLISH" : pct >= 40 ? "NEUTRAL" : "BEARISH";
  return (
    <div className="flex items-center gap-3 h-full">
      <div className="relative grid place-items-center flex-shrink-0" style={{ width: 84, height: 84 }}>
        <svg width="84" height="84" className="-rotate-90">
          <circle cx="42" cy="42" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
          <circle cx="42" cy="42" r={r} fill="none" stroke={col} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)}
            style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.4s", filter: `drop-shadow(0 0 6px ${col})` }} />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-lg font-black font-mono text-white leading-none">{Math.round(pct)}</span>
          <span className="text-[7.5px] font-mono tracking-[0.12em]" style={{ color: col }}>{label}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 flex-1">
        {subs.map((s) => (
          <div key={s.k} className="flex flex-col">
            <span className="text-[8px] font-mono uppercase tracking-[0.12em] text-zinc-500">{s.k}</span>
            <span className="text-xs font-bold font-mono" style={{ color: s.v >= 55 ? C.teal : s.v >= 45 ? C.neutral : C.red }}>{Math.round(s.v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────────────────────── order-flow mini line ───────────────────────── */
const OrderFlow = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart data={data} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
      <Line type="monotone" dataKey="buy" stroke={C.teal} strokeWidth={1.6} dot={false} isAnimationActive={false} />
      <Line type="monotone" dataKey="sell" stroke={C.red} strokeWidth={1.6} dot={false} isAnimationActive={false} />
    </LineChart>
  </ResponsiveContainer>
);

/* ───────────────────────── liquidity heatmap ───────────────────────── */
const LiquidityMap = ({ cells }) => (
  <div className="grid h-full w-full gap-[2px]" style={{ gridTemplateColumns: `repeat(${Math.max(1, cells.length)}, 1fr)` }}>
    {cells.map((col, i) => (
      <div key={i} className="flex flex-col gap-[2px] justify-end">
        {col.map((v, j) => (
          <div key={j} className="flex-1 rounded-[1px]" style={{
            background: v > 0 ? `rgba(0,229,181,${0.12 + v * 0.7})` : `rgba(255,42,42,${0.12 + Math.abs(v) * 0.7})`,
          }} />
        ))}
      </div>
    ))}
  </div>
);

/* ───────────────────────── future outlook mini ───────────────────────── */
const FutureOutlook = ({ forecast }) => {
  const w = 220, h = 90, x0 = 8, y0 = h / 2;
  const scs = forecast?.scenarios || [];
  const anchor = forecast?.anchor_price;
  const em = forecast?.expected_move || 1;
  const yOf = (p) => (anchor == null ? y0 : y0 - ((p - anchor) / (em * 2)) * (h * 0.42));
  const cmap = { BULL: C.teal, BASE: C.neutral, BEAR: C.red };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
      {["NOW", "+1H", "+2H", "+3H"].map((t, i) => (
        <text key={t} x={x0 + (i / 3) * (w - 24)} y={h - 2} fill="#4b5563" fontSize="7" fontFamily="JetBrains Mono">{t}</text>
      ))}
      {scs.map((sc) => {
        const yt = Math.max(6, Math.min(h - 12, yOf(sc.target)));
        const xe = w - 16;
        return (
          <g key={sc.type}>
            <path d={`M ${x0} ${y0} Q ${(x0 + xe) / 2} ${(y0 + yt) / 2} ${xe} ${yt}`} fill="none"
              stroke={cmap[sc.type]} strokeWidth={sc.type === forecast.primary ? 2 : 1.2}
              opacity={sc.type === forecast.primary ? 0.95 : 0.5} style={{ filter: `drop-shadow(0 0 4px ${cmap[sc.type]}88)` }} />
            <text x={xe - 1} y={yt - 3} textAnchor="end" fill={cmap[sc.type]} fontSize="7" fontFamily="JetBrains Mono">
              {sc.type === "BULL" ? "BULLISH" : sc.type === "BEAR" ? "BEARISH" : "NEUTRAL"}
            </text>
          </g>
        );
      })}
      <line x1={x0} y1={6} x2={x0} y2={h - 10} stroke={C.acid} strokeWidth="1" opacity="0.5" strokeDasharray="2 3" />
    </svg>
  );
};

/* ═══════════════════════════ main page ═══════════════════════════ */
export default function LiquidEngine() {
  const nav = useNavigate();
  const { state, connected } = useAgentSocket();
  const [selected, setSelected] = useState("ES");
  const [tf, setTf] = useState("15m");
  const [bars, setBars] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [news, setNews] = useState([]);
  const [railActive, setRailActive] = useState("overview");
  const [symMenu, setSymMenu] = useState(false);
  const [view, setView] = useState({ count: 90, pan: 0 });

  const market = state?.market || [];
  const m = market.find((x) => x.symbol === selected) || market[0] || {};

  /* data loading */
  useEffect(() => {
    let alive = true;
    const load = () => {
      api.chart(selected, TF_MAP[tf] || "15m").then((d) => { if (alive) setBars(d.bars || []); }).catch(() => {});
      api.forecast(selected, TF_MAP[tf] || "15m", false).then((r) => { if (alive) setForecast(r?.forecast || null); }).catch(() => {});
    };
    setBars([]); setForecast(null); load();
    const id = setInterval(load, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [selected, tf]);
  useEffect(() => { setView({ count: 90, pan: 0 }); }, [selected, tf]);
  useEffect(() => { api.news("", "").then((r) => setNews(r?.news || r?.data || [])).catch(() => {}); }, []);

  /* container sizing */
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((e) => {
      const r = e[0].contentRect;
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  /* geometry / scales (shared by canvas + overlays) */
  const geom = useMemo(() => {
    const total = bars.length;
    if (!total || !size.w || !size.h) return null;
    const PADR = 72, PADT = 10, PADB = 12;
    const plotW = size.w - PADR;
    const plotH = size.h - PADT - PADB;
    const count = Math.max(8, Math.min(view.count, total));
    const maxPan = Math.max(0, total - count);
    const pan = Math.max(0, Math.min(view.pan, maxPan));
    const start = total - count - pan;
    const iLo = Math.max(0, Math.floor(start));
    const iHi = Math.min(total, Math.ceil(start + count) + 1);
    const data = bars.slice(iLo, iHi);
    if (!data.length) return null;
    const pastW = plotW * 0.66;
    const cw = pastW / count;
    const xOf = (k) => (k + 0.5 - start) * cw;
    const nowX = xOf(total - 1);
    let hi = Math.max(...data.map((b) => b.h));
    let lo = Math.min(...data.map((b) => b.l));
    (forecast?.scenarios || []).forEach((s) => { if (s.target != null) { hi = Math.max(hi, s.target); lo = Math.min(lo, s.target); } });
    if (forecast?.anchor_price != null) { hi = Math.max(hi, forecast.anchor_price); lo = Math.min(lo, forecast.anchor_price); }
    const pad = (hi - lo) * 0.1 || 1;
    hi += pad; lo -= pad;
    const y = (p) => PADT + ((hi - p) / (hi - lo)) * plotH;
    return { total, data, iLo, start, count, cw, xOf, nowX, hi, lo, y, plotW, plotH, pastW, PADT, PADR, PADB, size };
  }, [bars, forecast, view, size]);

  const geomRef = useRef(geom);
  useEffect(() => { geomRef.current = geom; }, [geom]);

  const up = (m?.change || 0) >= 0;

  /* ── canvas liquid stream ── */
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf;
    // seed particles
    if (particlesRef.current.length === 0) {
      particlesRef.current = Array.from({ length: 80 }, () => ({
        p: Math.random(), sp: 0.0009 + Math.random() * 0.0022, sz: 0.6 + Math.random() * 1.8, jit: (Math.random() - 0.5) * 6,
      }));
    }
    const bubbles = Array.from({ length: 14 }, () => ({ x: Math.random(), y: Math.random(), sp: 0.0006 + Math.random() * 0.0014, r: 1 + Math.random() * 3 }));

    const draw = () => {
      const g = geomRef.current;
      const dpr = window.devicePixelRatio || 1;
      const w = size.w, h = size.h;
      if (cv.width !== w * dpr || cv.height !== h * dpr) { cv.width = w * dpr; cv.height = h * dpr; }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (!g) { raf = requestAnimationFrame(draw); return; }

      // faint grid
      ctx.strokeStyle = "rgba(255,255,255,0.035)";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const yy = g.PADT + (g.plotH / 5) * i;
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(g.plotW, yy); ctx.stroke();
      }

      // build close polyline in screen coords
      const pts = g.data.map((b, i) => [g.xOf(g.iLo + i), g.y(b.c)]);
      if (pts.length < 2) { raf = requestAnimationFrame(draw); return; }

      // gradient across the stream
      const grad = ctx.createLinearGradient(0, 0, g.plotW, 0);
      grad.addColorStop(0, C.cyan);
      grad.addColorStop(0.5, C.teal);
      grad.addColorStop(0.82, up ? C.cyan : C.magenta);
      grad.addColorStop(1, up ? C.teal : C.red);

      // area fill
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) {
        const xc = (pts[i][0] + pts[i - 1][0]) / 2, yc = (pts[i][1] + pts[i - 1][1]) / 2;
        ctx.quadraticCurveTo(pts[i - 1][0], pts[i - 1][1], xc, yc);
      }
      ctx.lineTo(pts[pts.length - 1][0], g.PADT + g.plotH);
      ctx.lineTo(pts[0][0], g.PADT + g.plotH);
      ctx.closePath();
      const fillGrad = ctx.createLinearGradient(0, g.PADT, 0, g.PADT + g.plotH);
      fillGrad.addColorStop(0, up ? "rgba(0,229,181,0.22)" : "rgba(255,42,42,0.18)");
      fillGrad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = fillGrad; ctx.fill();

      // glowing line
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) {
        const xc = (pts[i][0] + pts[i - 1][0]) / 2, yc = (pts[i][1] + pts[i - 1][1]) / 2;
        ctx.quadraticCurveTo(pts[i - 1][0], pts[i - 1][1], xc, yc);
      }
      ctx.strokeStyle = grad; ctx.lineWidth = 2.2; ctx.lineJoin = "round"; ctx.lineCap = "round";
      ctx.shadowColor = up ? C.teal : C.magenta; ctx.shadowBlur = 16;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // helper: point at fraction f along polyline
      const at = (f) => {
        const t = f * (pts.length - 1);
        const i = Math.min(pts.length - 2, Math.floor(t));
        const fr = t - i;
        return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * fr, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * fr];
      };
      // flowing particles
      particlesRef.current.forEach((pt) => {
        pt.p += pt.sp; if (pt.p > 1) pt.p -= 1;
        const [px, py] = at(pt.p);
        ctx.beginPath();
        ctx.arc(px, py + pt.jit, pt.sz, 0, Math.PI * 2);
        ctx.fillStyle = up ? "rgba(180,255,240,0.9)" : "rgba(255,190,220,0.9)";
        ctx.shadowColor = up ? C.cyan : C.magenta; ctx.shadowBlur = 8;
        ctx.fill(); ctx.shadowBlur = 0;
      });
      // ambient bubbles
      bubbles.forEach((b) => {
        b.y -= b.sp; if (b.y < 0) { b.y = 1; b.x = Math.random(); }
        const bx = b.x * g.pastW, by = g.PADT + b.y * g.plotH;
        ctx.beginPath(); ctx.arc(bx, by, b.r, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,240,255,0.12)"; ctx.lineWidth = 1; ctx.stroke();
      });

      // future tendrils
      const anchor = forecast?.anchor_price;
      const pan0 = view.pan < 1;
      if (anchor != null && pan0 && forecast?.scenarios) {
        const ax = g.nowX, ay = g.y(anchor), xe = g.plotW - 4;
        const cmap = { BULL: C.teal, BASE: C.neutral, BEAR: C.red };
        forecast.scenarios.forEach((sc) => {
          const yt = g.y(sc.target);
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.quadraticCurveTo((ax + xe) / 2, (ay + yt) / 2, xe, yt);
          ctx.strokeStyle = cmap[sc.type];
          ctx.globalAlpha = sc.type === forecast.primary ? 0.9 : 0.4;
          ctx.lineWidth = sc.type === forecast.primary ? 2.2 : 1.3;
          ctx.setLineDash([6, 5]);
          ctx.shadowColor = cmap[sc.type]; ctx.shadowBlur = sc.type === forecast.primary ? 12 : 0;
          ctx.stroke();
          ctx.setLineDash([]); ctx.globalAlpha = 1; ctx.shadowBlur = 0;
          // widening band
          const band = g.y(sc.range ? sc.range[sc.type === "BEAR" ? 0 : 1] : sc.target);
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.quadraticCurveTo((ax + xe) / 2, (ay + band) / 2, xe, band);
          ctx.lineTo(xe, yt);
          ctx.quadraticCurveTo((ax + xe) / 2, (ay + yt) / 2, ax, ay);
          ctx.closePath();
          ctx.fillStyle = cmap[sc.type]; ctx.globalAlpha = 0.06; ctx.fill(); ctx.globalAlpha = 1;
        });
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size, forecast, up, view.pan]);

  /* ── zoom + pan interactions ── */
  const zoomRef = useRef(view.count), panRef = useRef(view.pan), lenRef = useRef(0);
  useEffect(() => { zoomRef.current = view.count; }, [view.count]);
  useEffect(() => { panRef.current = view.pan; }, [view.pan]);
  useEffect(() => { lenRef.current = bars.length; }, [bars.length]);
  const overlayRef = useRef(null);
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const total = lenRef.current; if (!total) return;
      const g = geomRef.current; if (!g) return;
      const r = el.getBoundingClientRect();
      const fx = Math.max(0, Math.min(1, (e.clientX - r.left) / g.pastW));
      const z = zoomRef.current, p = panRef.current;
      const count = Math.max(8, Math.min(z, total));
      const start = total - count - Math.max(0, Math.min(p, Math.max(0, total - count)));
      const idxUnder = start + fx * count;
      const factor = Math.max(0.5, Math.min(2, Math.exp(e.deltaY * 0.0015)));
      const nz = Math.max(10, Math.min(400, z * factor));
      const nc = Math.max(8, Math.min(nz, total));
      const nStart = idxUnder - fx * nc;
      let nPan = total - nc - nStart;
      nPan = Math.max(0, Math.min(nPan, Math.max(0, total - nc)));
      setView({ count: nz, pan: nPan });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [bars.length > 0, size.w]);
  const onPointerDown = (e) => {
    const g = geomRef.current; if (!g) return;
    const startX = e.clientX, startPan = panRef.current, total = lenRef.current;
    const maxPan = Math.max(0, total - Math.max(8, Math.min(zoomRef.current, total)));
    const move = (ev) => {
      const dBars = ((ev.clientX - startX) / g.pastW) * zoomRef.current;
      setView((v) => ({ ...v, pan: Math.max(0, Math.min(maxPan, startPan + dBars)) }));
    };
    const up2 = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up2); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up2);
  };

  /* ── derived on-chart events ── */
  const events = useMemo(() => {
    if (!geom) return [];
    const g = geom, out = [];
    const fmtTime = (b, gi) => {
      const ts = b?.ts ?? b?.t;
      if (typeof ts === "number" && ts > 1e9) { const d = new Date(ts * (ts > 1e12 ? 1 : 1000)); return d.toTimeString().slice(0, 5); }
      return `T-${g.total - 1 - gi}`;
    };
    // biggest move in window
    let bi = 0, bmax = 0;
    g.data.forEach((b, i) => { const bd = Math.abs(b.c - b.o); if (bd > bmax) { bmax = bd; bi = i; } });
    const bb = g.data[bi];
    if (bb) out.push({ gi: g.iLo + bi, price: bb.c, label: bb.c >= bb.o ? "LARGE BUY" : "LARGE SELL", col: bb.c >= bb.o ? C.teal : C.red, time: fmtTime(bb, g.iLo + bi), up: bb.c >= bb.o });
    // liquidity sweep
    if (m?.sweep?.level != null) out.push({ gi: g.total - 4, price: m.sweep.level, label: "LIQUIDITY SWEEP", col: C.acid, time: "recent", up: m.sweep.side === "LONG" });
    // structure break
    const st = m?.structure;
    if (st && (st.type || typeof st === "string")) {
      const b2 = g.data[Math.max(0, g.data.length - 3)];
      if (b2) out.push({ gi: g.total - 3, price: b2.c, label: "BREAK OF STRUCTURE", col: C.magenta, time: "recent", up: b2.c >= b2.o });
    }
    // filter to visible past region and de-dup by rough y
    return out.filter((e) => { const x = g.xOf(e.gi); return x > 40 && x < g.nowX + 4 && e.price >= g.lo && e.price <= g.hi; }).slice(0, 4);
  }, [geom, m]);

  /* ── bottom-panel derived datasets ── */
  const orderFlow = useMemo(() => {
    const src = bars.slice(-60);
    let buy = 0, sell = 0;
    return src.map((b) => { const d = b.c - b.o; if (d >= 0) buy += d; else sell += -d; return { buy: buy, sell: sell }; });
  }, [bars]);
  const liq = useMemo(() => {
    const src = bars.slice(-40);
    return src.map((b) => {
      const d = b.c - b.o; const rows = 6;
      return Array.from({ length: rows }, (_, j) => {
        const v = (d / ((b.h - b.l) || 1)) * (1 - Math.abs(j - rows / 2) / rows);
        return Math.max(-1, Math.min(1, v * 1.5));
      });
    });
  }, [bars]);
  const sentiment = useMemo(() => {
    const bias = forecast?.bias ?? 0;
    const conf = forecast?.confidence ?? 0.5;
    const score = 50 + bias * 50;
    const trend = m?.trend?.bias === "BULLISH" ? 70 : m?.trend?.bias === "BEARISH" ? 30 : 50;
    return {
      score,
      subs: [
        { k: "TREND", v: trend },
        { k: "MOMENTUM", v: 50 + bias * 40 },
        { k: "STRUCTURE", v: 50 + (m?.direction === "LONG" ? 22 : m?.direction === "SHORT" ? -22 : 0) },
        { k: "CONF", v: conf * 100 },
      ],
    };
  }, [forecast, m]);
  const calEvents = useMemo(() => {
    const items = (news || []).slice(0, 4).map((n) => ({
      time: (n.published_at || n.date || "").slice(11, 16) || "—",
      label: n.title || n.headline || n.description || "Market update",
      impact: "MEDIUM",
    }));
    if (!items.length) return events.map((e) => ({ time: e.time, label: e.label, impact: "HIGH" }));
    return items;
  }, [news, events]);

  /* ── header ── */
  const changePct = m?.change_pct;
  const marketState = up ? "BULLISH MOMENTUM" : "BEARISH PRESSURE";

  return (
    <div className="h-screen w-screen flex overflow-hidden r2-bg text-white selection:bg-[#38BDF8] selection:text-black" style={{ fontFamily: "Manrope, sans-serif" }}>
      <LeftRail active={railActive} onNav={setRailActive} coreMode={!connected ? "offline" : (state?.pending || []).length ? "executing" : (state?.pme?.positions || []).length ? "position" : (state?.ai?.running || []).length ? "analyzing" : state?.config?.running ? "scanning" : "idle"} />

      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* HEADER */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 h-[92px] border-b border-white/10 bg-gradient-to-b from-black/70 to-transparent z-30">
          <div className="flex items-center gap-6">
            <button onClick={() => nav("/v2")} data-testid="engine-back-btn" className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-zinc-400 hover:text-[#38BDF8] hover:border-[#38BDF8]/40 transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div className="relative">
              <button onClick={() => setSymMenu((s) => !s)} data-testid="engine-symbol-btn" className="flex items-center gap-2 group">
                <div className="flex flex-col items-start">
                  <span className="text-2xl font-black tracking-tight leading-none" style={{ fontFamily: "Unbounded, sans-serif" }}>{m?.symbol || selected}</span>
                  <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-zinc-500 mt-0.5">{(m?.name || "").replace(/\(.*\)/, "") || "Futures"}</span>
                </div>
                <ChevronDown size={15} className="text-zinc-500 group-hover:text-zinc-300 mt-0.5" />
              </button>
              {symMenu && (
                <div className="absolute left-0 top-full mt-2 w-40 rounded-lg border border-white/10 bg-black/90 backdrop-blur-xl p-1 z-50" data-testid="engine-symbol-menu">
                  {market.map((s) => (
                    <button key={s.symbol} data-testid={`engine-sym-${s.symbol}`} onClick={() => { setSelected(s.symbol); setSymMenu(false); }}
                      className={`w-full text-left px-2.5 py-1.5 rounded text-xs font-mono transition-colors ${s.symbol === selected ? "bg-[#38BDF8]/15 text-[#38BDF8]" : "text-zinc-400 hover:bg-white/5"}`}>
                      {s.symbol} <span className="text-zinc-600">· {fmt(s.price, dec(s.price))}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-4xl font-black font-mono tracking-tighter leading-none" style={{ color: up ? C.teal : C.red }} data-testid="engine-price">
                {fmt(m?.price, dec(m?.price))}
              </span>
              <span className="text-xs font-mono mt-1" style={{ color: up ? C.teal : C.red }}>
                {m?.change >= 0 ? "+" : ""}{fmt(m?.change, dec(m?.change))} ({changePct >= 0 ? "+" : ""}{fmt(changePct, 2)}%)
              </span>
            </div>
            <div className="flex flex-col gap-2 pl-6 border-l border-white/10">
              <div>
                <div className="text-[8.5px] font-mono uppercase tracking-[0.2em] text-zinc-500">Market State</div>
                <div className="text-xs font-bold" style={{ color: up ? C.teal : C.red }}>{marketState}</div>
              </div>
              <div>
                <div className="text-[8.5px] font-mono uppercase tracking-[0.2em] text-zinc-500">Data</div>
                <div className="text-xs font-bold font-mono text-zinc-300">{m?.source || "SIM"}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-[11px] font-mono">
                <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-[#00E5B5]" : "bg-red-500"}`} style={{ boxShadow: connected ? `0 0 8px ${C.teal}` : "none" }} />
                <span style={{ color: connected ? C.teal : "#f87171" }}>{connected ? "LIVE" : "RECONNECTING"}</span>
              </div>
              <Radio size={14} className="text-zinc-600" />
            </div>
            <div className="flex items-center gap-0.5 rounded-lg border border-white/10 bg-black/40 p-0.5">
              {TFS.map((t) => {
                const usable = !!TF_MAP[t];
                return (
                  <button key={t} data-testid={`engine-tf-${t}`} disabled={!usable}
                    onClick={() => usable && setTf(t)}
                    className={`px-2 py-1 rounded text-[10px] font-mono font-semibold transition-colors ${tf === t ? "bg-[#38BDF8]/20 text-[#38BDF8]" : usable ? "text-zinc-400 hover:text-white" : "text-zinc-700 cursor-not-allowed"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* CHART + OVERLAYS */}
        <div className="flex-1 relative min-h-0" ref={wrapRef}>
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ width: "100%", height: "100%" }} data-testid="liquid-canvas" />
          {/* interaction overlay */}
          <div ref={overlayRef} onPointerDown={onPointerDown} className="absolute inset-0 cursor-crosshair" style={{ touchAction: "none" }} data-testid="liquid-overlay" />

          {/* right price axis */}
          {geom && [0, 1, 2, 3, 4, 5].map((i) => {
            const p = geom.hi - ((geom.hi - geom.lo) / 5) * i;
            return (
              <div key={i} className="absolute right-2 text-[10px] font-mono text-zinc-500 pointer-events-none" style={{ top: geom.y(p) - 6 }}>
                {fmt(p, dec(p))}
              </div>
            );
          })}

          {/* NOW divider */}
          {geom && view.pan < 1 && geom.nowX < geom.plotW && (
            <div className="absolute pointer-events-none" style={{ left: geom.nowX, top: geom.PADT, height: geom.plotH }}>
              <div className="h-full w-px" style={{ background: C.acid, boxShadow: `0 0 10px ${C.acid}` }} />
              <div className="absolute -top-0.5 -translate-x-1/2 left-0 px-1.5 py-0.5 rounded text-[8px] font-mono font-bold text-black" style={{ background: C.acid }}>NOW</div>
            </div>
          )}

          {/* event callouts */}
          {geom && events.map((e, i) => {
            const x = geom.xOf(e.gi), y = geom.y(e.price);
            const labelY = e.up ? y - 54 : y + 20;
            return (
              <div key={i} data-testid={`event-callout-${i}`} className="absolute pointer-events-none" style={{ left: x, top: Math.min(y, labelY) }}>
                <div className="absolute w-px bg-white/25" style={{ left: 0, top: e.up ? 0 : 0, height: 34 }} />
                <div className="absolute h-2 w-2 rounded-full" style={{ left: -3.5, top: e.up ? 34 : 0, background: e.col, boxShadow: `0 0 8px ${e.col}` }} />
                <div className="absolute whitespace-nowrap rounded-md border border-white/10 bg-black/70 backdrop-blur-md px-2 py-1" style={{ left: 6, top: e.up ? -6 : 40 }}>
                  <div className="text-[8px] font-mono text-zinc-500">{e.time}</div>
                  <div className="text-[9px] font-mono font-bold" style={{ color: e.col }}>{e.label}</div>
                </div>
              </div>
            );
          })}

          {/* right forecast cards */}
          {forecast?.scenarios && (
            <div className="absolute right-4 top-4 flex flex-col gap-3 w-64 z-20 pointer-events-none" data-testid="engine-forecast-cards">
              {["BULL", "BASE", "BEAR"].map((tk, i) => {
                const sc = forecast.scenarios.find((s) => s.type === tk);
                return sc ? <ForecastCard key={tk} sc={sc} confidence={forecast.confidence} primary={forecast.primary} i={i} /> : null;
              })}
            </div>
          )}

          {/* zoom hint */}
          <div className="absolute left-4 bottom-4 z-20 text-[9px] font-mono text-zinc-600 pointer-events-none">scroll to zoom · drag to pan</div>
          {!geom && <div className="absolute inset-0 grid place-items-center text-zinc-600 text-xs font-mono">rendering liquid stream…</div>}
        </div>

        {/* BOTTOM ANALYTICS */}
        <div className="flex-shrink-0 h-44 grid grid-cols-5 gap-3 p-3 border-t border-white/10 bg-black/50 backdrop-blur-xl z-30" data-testid="engine-bottom-analytics">
          <PanelShell title="Liquidity Map" testid="panel-liquidity"><LiquidityMap cells={liq} /></PanelShell>
          <PanelShell title="Order Flow" testid="panel-orderflow"><OrderFlow data={orderFlow} /></PanelShell>
          <PanelShell title="Market Sentiment" testid="panel-sentiment"><SentimentGauge score={sentiment.score} subs={sentiment.subs} /></PanelShell>
          <PanelShell title="Event Calendar" testid="panel-calendar">
            <div className="flex flex-col gap-1.5 overflow-hidden">
              {calEvents.map((ev, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-[9px] font-mono text-zinc-500 w-8 flex-shrink-0 pt-0.5">{ev.time}</span>
                  <span className="text-[10px] text-zinc-300 leading-tight truncate flex-1">{ev.label}</span>
                  <span className="h-1.5 w-1.5 rounded-full flex-shrink-0 mt-1" style={{ background: ev.impact === "HIGH" ? C.red : C.acid }} />
                </div>
              ))}
              {!calEvents.length && <span className="text-[10px] text-zinc-600 font-mono">no events</span>}
            </div>
          </PanelShell>
          <PanelShell title="Future Outlook 3D" testid="panel-outlook"><FutureOutlook forecast={forecast} /></PanelShell>
        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 h-7 border-t border-white/10 bg-black/60 text-[8.5px] font-mono uppercase tracking-[0.18em] text-zinc-600">
          <span>Data: {connected ? "Real-Time" : "Reconnecting"} · Source {m?.source || "SIM"}</span>
          <span className="text-zinc-500">Powered by Ripp Labs · Liquid Intelligence Engine</span>
          <span>Quality: {m?.source === "BROKER" ? "Premium" : m?.source === "MARKET" ? "Standard" : "Sim"}</span>
        </div>
      </div>
    </div>
  );
}
